<script setup>
import { ref, computed, provide, watch, nextTick } from 'vue';
import { Virtualizer } from 'virtua/vue';
import { useBreakpoints } from '@vueuse/core';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import ConversationItem from './ConversationItem.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';

import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  conversationList: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  showEndOfListMessage: { type: Boolean, default: false },
  label: { type: String, default: '' },
  teamId: { type: [String, Number], default: 0 },
  priority: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  conversationType: { type: String, default: '' },
  showAssignee: { type: Boolean, default: false },
  isOnExpandedLayout: { type: Boolean, default: false },
});

const emit = defineEmits(['loadMore']);

const conversationListRef = ref(null);
const virtualListRef = ref(null);
const isContextMenuOpen = ref(false);
const didStartInitialLoad = ref(false);
const didResolveInitialLoad = ref(false);

provide('contextMenuElementTarget', virtualListRef);

const breakpoints = useBreakpoints({
  lg: wootConstants.LARGE_SCREEN_BREAKPOINT,
});
const isLgScreen = breakpoints.greaterOrEqual('lg');
const showExpandedCards = computed(
  () => props.isOnExpandedLayout && isLgScreen.value
);
const hasConversations = computed(() => props.conversationList.length > 0);
const firstConversationId = computed(() => props.conversationList[0]?.id);

const markInitialListReady = async (attempt = 0) => {
  await nextTick();

  const firstConversationTarget = document.querySelector(
    '#dashboard-inbox-first-conversation'
  );

  if (!hasConversations.value || firstConversationTarget || attempt >= 20) {
    didResolveInitialLoad.value = true;
    return;
  }

  setTimeout(() => markInitialListReady(attempt + 1), 50);
};

watch(
  () => props.isLoading,
  isLoading => {
    if (isLoading) {
      didStartInitialLoad.value = true;
      return;
    }

    if (didStartInitialLoad.value) {
      markInitialListReady();
    }
  },
  { immediate: true }
);

watch(
  hasConversations,
  hasItems => {
    if (!hasItems) return;

    markInitialListReady();
  },
  { immediate: true }
);

useChatListKeyboardEvents(conversationListRef);

const intersectionObserverOptions = computed(() => ({
  root: conversationListRef.value,
  rootMargin: '100px 0px 100px 0px',
}));

const onContextMenuToggle = state => {
  isContextMenuOpen.value = state;
};

const loadMoreConversations = () => {
  emit('loadMore');
};

provide('toggleContextMenu', onContextMenuToggle);

defineExpose({ conversationListRef });
</script>

<template>
  <div
    ref="conversationListRef"
    class="flex-1 min-h-0 overflow-y-auto conversations-list"
    :class="{ '!overflow-hidden': isContextMenuOpen }"
  >
    <Virtualizer
      ref="virtualListRef"
      v-slot="{ item }"
      :data="conversationList"
      class="[&>div:has(+_div_.active)>*]:!border-n-surface-1 [&>div:has(+_div_.selected)>*]:!border-n-surface-1"
    >
      <ConversationItem
        :source="item"
        :label="label"
        :team-id="teamId"
        :priority="priority"
        :folders-id="foldersId"
        :conversation-type="conversationType"
        :show-assignee="showAssignee"
        :show-expanded="showExpandedCards"
        :tour-target-id="
          item.id === firstConversationId
            ? 'dashboard-inbox-first-conversation'
            : ''
        "
      />
    </Virtualizer>
    <div
      v-if="isLoading"
      id="dashboard-inbox-conversation-list-loading"
      class="flex justify-center my-4"
    >
      <Spinner class="text-n-brand" />
    </div>
    <span
      v-if="didResolveInitialLoad"
      id="dashboard-inbox-conversation-list-ready"
      class="hidden"
    />
    <span
      v-if="didResolveInitialLoad && !hasConversations"
      id="dashboard-inbox-conversation-list-empty"
      class="hidden"
    />
    <p v-else-if="showEndOfListMessage" class="p-4 text-center text-n-slate-11">
      {{ $t('CHAT_LIST.EOF') }}
    </p>
    <IntersectionObserver
      v-else
      :options="intersectionObserverOptions"
      @observed="loadMoreConversations"
    />
  </div>
</template>
